import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import CapacitorStorage from './CapacitorStorage';
import { authService } from './AuthService';
import type { AppNotification, NotificationLocalState } from 'Types/notifications';
import { getCurrentAppVersion } from './appVersion';

const notificationsBaseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/notifications`;

const notificationStorageKey = (userId: string, notificationId: number) =>
  `notification:${userId}:${notificationId}`;

// Auth is attached by the axios interceptor; these are metadata headers only.
async function getNotificationHeaders() {
  if (authService.getSnapshot().status !== 'authenticated') {
    return null;
  }

  const appVersion = await getCurrentAppVersion();

  return {
    'x-museflow-platform': Capacitor.getPlatform(),
    ...(appVersion ? { 'x-museflow-app-version': appVersion } : {}),
  };
}

export async function fetchActiveAppNotification(): Promise<AppNotification | null> {
  const headers = await getNotificationHeaders();
  if (!headers) {
    return null;
  }

  const response = await axios.get<AppNotification | null>(`${notificationsBaseUrl}/active`, {
    headers,
  });

  return response.data;
}

export async function recordNotificationImpression(notificationId: number): Promise<void> {
  const headers = await getNotificationHeaders();
  if (!headers) return;

  await axios.post(`${notificationsBaseUrl}/${notificationId}/impression`, {}, { headers });
}

export async function recordNotificationDismiss(notificationId: number): Promise<void> {
  const headers = await getNotificationHeaders();
  if (!headers) return;

  await axios.post(`${notificationsBaseUrl}/${notificationId}/dismiss`, {}, { headers });
}

export async function recordNotificationCtaClick(notificationId: number): Promise<void> {
  const headers = await getNotificationHeaders();
  if (!headers) return;

  await axios.post(`${notificationsBaseUrl}/${notificationId}/cta-click`, {}, { headers });
}

export async function getLocalNotificationState(
  userId: string,
  notificationId: number,
): Promise<NotificationLocalState | null> {
  const raw = await CapacitorStorage.getItem(notificationStorageKey(userId, notificationId));
  if (!raw) return null;

  try {
    return JSON.parse(raw) as NotificationLocalState;
  } catch (error) {
    console.warn('Failed to parse local notification state:', error);
    await CapacitorStorage.removeItem(notificationStorageKey(userId, notificationId));
    return null;
  }
}

export async function saveLocalNotificationState(
  userId: string,
  notificationId: number,
  patch: Partial<NotificationLocalState>,
): Promise<NotificationLocalState> {
  const current = (await getLocalNotificationState(userId, notificationId)) || {};
  const next = {
    ...current,
    ...patch,
  };

  await CapacitorStorage.setItem(
    notificationStorageKey(userId, notificationId),
    JSON.stringify(next),
  );
  return next;
}
